import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import useSugestoes from "@/hooks/useSugestoes";
import { filtrarSugestoes, semAcento } from "@/lib/sugestoes";

// Lote 126 (Robson, 25/10/2026) — "em todos os campos de preenchimento, ao identificar palavras que já constam
// dos cadastros, sugerir o preenchimento automático (aparece a sugestão para clicar)".
// Mostra até 5 sugestões logo abaixo do campo a partir de 2 letras; 1 toque preenche. Texto livre continua valendo:
// ignorar a lista e seguir digitando não atrapalha. Só lê valores que o próprio usuário já cadastrou.
// Destaca em negrito o trecho digitado (só quando dá para localizar sem acento).
function Destaque({ texto, busca, digitos }) {
  if (digitos) return <>{texto}</>;
  const t = String(texto);
  const q = semAcento(busca).trim();
  const i = semAcento(t).indexOf(q);
  if (!q || i < 0 || semAcento(t).length !== t.length) return <>{t}</>;
  return <>{t.slice(0, i)}<b className="font-semibold text-stone-900">{t.slice(i, i + q.length)}</b>{t.slice(i + q.length)}</>;
}

/**
 * <SugestaoInput fonte="comarcas" value={...} onValueChange={(texto) => ...} />
 * `fonte`: comarcas | varas | partes | apelidos | numeros | clientes   (ou `lista` com valores próprios)
 * `transformar`: máscara aplicada ao digitar e ao escolher (ex.: maskCaseNumberInput)
 * `aoEscolher(valor)`: chamado depois que a pessoa toca numa sugestão.
 */
export default function SugestaoInput({ fonte, lista, value, onValueChange, transformar, aoEscolher, fantasma, testid, className, onFocus, onBlur, onKeyDown, ...resto }) {
  const dados = useSugestoes();
  // `fonte` pode ser uma lista de fontes (ex.: ["partes", "clientes"]) — juntadas sem repetir.
  const base = useMemo(() => {
    if (lista) return lista;
    if (Array.isArray(fonte)) {
      const vistos = new Map();
      fonte.forEach((f) => (dados[f] || []).forEach((v) => { if (!vistos.has(semAcento(v))) vistos.set(semAcento(v), v); }));
      return [...vistos.values()];
    }
    return dados[fonte] || [];
  }, [lista, fonte, dados]);
  const digitos = fonte === "numeros";
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(-1);
  const caixa = useRef(null);
  const idLista = useId();
  const opcoes = useMemo(
    () => filtrarSugestoes(base, value, { digitos }),
    [base, value, digitos],
  );
  const mostrar = aberto && opcoes.length > 0;
  useEffect(() => { setAtivo(-1); }, [value]);

  const aplicar = (v) => {
    const novo = transformar ? transformar(v) : v;
    onValueChange?.(novo);
    setAberto(false);
    aoEscolher?.(novo);
  };

  const resto_fantasma = fantasma && value && String(value).length < fantasma.length && !/[a-zA-Z]/.test(String(value))
    ? fantasma.slice(String(value).length) : "";
  return (
    <div className="relative" ref={caixa}>
      {resto_fantasma ? (
        // modelo em cinza claro atrás do campo: o que já foi digitado fica invisível (o campo escreve por cima)
        <div aria-hidden="true" data-testid={testid ? `${testid}-modelo` : undefined}
          className="absolute inset-0 flex items-center pl-[calc(0.75rem+1px)] pr-3 text-base md:text-sm text-stone-300 pointer-events-none whitespace-pre overflow-hidden">
          <span className="invisible">{value}</span><span>{resto_fantasma}</span>
        </div>
      ) : null}
      <Input
        {...resto}
        data-testid={testid}
        className={className}
        value={value}
        autoComplete="off"
        role="combobox"
        aria-expanded={mostrar}
        aria-controls={mostrar ? idLista : undefined}
        aria-autocomplete="list"
        onChange={(e) => { const v = transformar ? transformar(e.target.value) : e.target.value; onValueChange?.(v); setAberto(true); }}
        onFocus={(e) => { setAberto(true); onFocus?.(e); }}
        onBlur={(e) => { setAberto(false); onBlur?.(e); }}
        onKeyDown={(e) => {
          if (mostrar && e.key === "ArrowDown") { e.preventDefault(); setAtivo((a) => (a + 1) % opcoes.length); }
          else if (mostrar && e.key === "ArrowUp") { e.preventDefault(); setAtivo((a) => (a <= 0 ? opcoes.length - 1 : a - 1)); }
          else if (mostrar && e.key === "Enter" && ativo >= 0) { e.preventDefault(); aplicar(opcoes[ativo]); }
          onKeyDown?.(e);
        }}
      />
      {mostrar && (
        <ul
          id={idLista}
          role="listbox"
          data-testid={testid ? `${testid}-sugestoes` : "sugestoes"}
          className="absolute left-0 right-0 top-full mt-1 z-[60] rounded-md border border-stone-200 bg-white shadow-md overflow-hidden"
        >
          <li className="px-3 pt-1.5 pb-0.5 text-[10px] uppercase tracking-widest text-stone-400 select-none" aria-hidden="true">Já cadastrado</li>
          {opcoes.map((o, i) => (
            <li
              key={o}
              role="option"
              aria-selected={i === ativo}
              data-testid={testid ? `${testid}-sugestao-${i}` : undefined}
              // mousedown (não click) para o campo não perder o foco antes do toque ser registrado
              onMouseDown={(e) => { e.preventDefault(); aplicar(o); }}
              onTouchStart={() => {}}
              className={`px-3 min-h-[40px] flex items-center text-sm text-stone-700 cursor-pointer hover:bg-stone-100 ${i === ativo ? "bg-stone-100" : ""}`}
            >
              <span className="truncate"><Destaque texto={o} busca={value} digitos={digitos} /></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
